/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { AuthAmplifyService } from "src/app/shared/service/auth-amplify.service";
import { SharingDataService } from "src/app/shared/service/sharing-data.service";
import { TranslationService } from "src/app/shared/service/translation.service";
import { AppConstants } from "src/app/shared/constants/constants";
import { LanguageService } from "src/app/shared/service/language.service";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { StoreManagerService } from "src/app/shared/store-manager/services/store-manager.service";
import { AuthenticationService } from "src/app/shared/service/authentication.service";
import { TranslateModule } from "@ngx-translate/core";
import { ProfileSwitchComponent } from "src/app/modules/alias/profile-switch/profile-switch.component";
import { ClickOutsideDirective } from "src/app/shared/directives/click-out-side.directive";

@Component({
  selector: "app-main-nav",
  templateUrl: "./main-nav.component.html",
  styleUrls: ["./main-nav.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    ProfileSwitchComponent,
    ClickOutsideDirective,
  ],
})
export class MainNavComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authAmplifyService = inject(AuthAmplifyService);
  private readonly SharingData = inject(SharingDataService);
  private readonly _translationService = inject(TranslationService);
  private readonly _langauageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly _storeManagerService = inject(StoreManagerService);

  isMenuVisible: boolean = false;
  scrolled60: any;
  above60: boolean = false;
  aliasSelected: any;
  loggedInUser: Observable<any>;
  selectedLanguage: string = "en-GB";
  isMobileMenuVisible: boolean = false;
  isProfileVisible: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  profileImage: any;
  translationData: any;
  commonLabels = AppConstants.COMMON_TITLE_LABELS;
  cacheBustedUrl: string = "";
  _enableGoogleMaps: boolean = false;

  public showLanguageSelectionModal: boolean = false;
  public userLanguageForDisplay: string = "English";

  constructor() {
    this.loggedInUser = this.authenticationService.isLoggedIn();
    const locationFeatureFlags =
      this._translationService.featureFlags?.locations;
    this._enableGoogleMaps = locationFeatureFlags.enableGoogleMaps;

    this._langauageService.showLanguageSelectionModal
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((show: boolean) => {
        if (!show) {
          this.manageLanguageToDisplay();
        }
      });
  }

  private manageLanguageToDisplay() {
    let selectedLanguage =
      this._storeManagerService.getUserSelectedLanguageFromStore();
    if (!selectedLanguage) {
      selectedLanguage = "en-GB";
    }
    this.selectedLanguage = selectedLanguage;
    this.userLanguageForDisplay = this.getUserSelectedLanguage();
  }

  ngOnInit(): void {
    this.userLanguageForDisplay = this.getUserSelectedLanguage();

    this.SharingData.getAliasList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data) {
          this.aliasSelected = data;
        }
      });
    this.SharingData.getProfileDetailsClicked()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: any) => {
        if (value) {
          this.isProfileVisible = false;
        }
      });
    localStorage.getItem("profile");
    if (localStorage.getItem("profile")) {
      this.aliasSelected = true;
    }
    this.SharingData.getProfileChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data) {
          this.handleCachedUrl();
        }
      });
    this.handleCachedUrl();
  }

  handleCachedUrl() {
    this.profileImage = localStorage.getItem("profileImage");
    this.cacheBustedUrl = `${this.profileImage}?cb=${new Date().getTime()}`;
  }

  async logout() {
    this.removeIndexedDb();
    this.authAmplifyService.logout();
  }

  async removeIndexedDb() {
    const dbs = await window.indexedDB.databases();
    dbs.forEach((db: any) => {
      window.indexedDB.deleteDatabase(db.name);
    });
    this.toggleMobileMenu(); // menu should be hidden
  }

  public switchLanguage(language: string): void {
    this._storeManagerService.setUserLanguageToStore(language);
    const fullURL = window.location.href.split("/");
    fullURL[3] = language;
    window.location.href = fullURL.join("/");
  }

  toggleMobileMenu() {
    this.isMobileMenuVisible = false;
    this.SharingData.setMobileMenuStatus(false);
    this._langauageService.showLanguageModalState.set(false);
    if (sessionStorage.getItem("availableLocales")) {
      sessionStorage.removeItem("availableLocales");
    }
  }

  myProfile() {
    this.isProfileVisible = !this.isProfileVisible;
  }

  closeDialog(event: any) {
    event.stopPropagation();
    this.SharingData.setProfileSelected(true);
    this.isProfileVisible = false;
  }

  public getUserSelectedLanguage(): string {
    const defaultLang = () => {
      const lang = selectedLanguage === "de" ? "German" : "English";
      return lang;
    };
    const selectedLanguage =
      this._storeManagerService.getUserSelectedLanguageFromStore();
    const supportedLangList =
      this._translationService.supportedLanguagesForDisplay;
    const langName = supportedLangList.find(
      (lang: any) => lang.localeCode === selectedLanguage,
    );
    return langName?.localeDescription?.split("(")[0] ?? defaultLang();
  }

  public openLanguageSelection() {
    this._langauageService.showLanguageModalState.set(true);
  }
  /**
   * @description This method is used to reload the same active route on click of the same tab
   * For ex: If user is on any child route and clicks on the tab from header, it will bring the user
   * back to same main page
   */
  public reloadActiveRouteForLocations(): void {
    const initalMapData = this._storeManagerService.getMapDataOnLoad();
    this._storeManagerService.setCurrentMapDataToStore(initalMapData);
    const locationsPath = `/${this.selectedLanguage}/locations`;
    this.router.navigateByUrl("/").then(() => {
      this.router.navigateByUrl(locationsPath);
    });
  }
}
